/**
 * Phase 22 Plan 22-03 — WorkOrderSitesTab
 *
 * Renders the active WorkOrderSite rows for a WO detail page.
 * Exposes "Add Sites" button (opens AddSitesDialog) and per-row
 * "Remove" button (opens RemoveSiteConfirmDialog).
 */

import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AddSitesDialog } from '@/components/work-orders/AddSitesDialog';
import { RemoveSiteConfirmDialog } from '@/components/work-orders/RemoveSiteConfirmDialog';
import { isActiveSiteLink, type WorkOrderDetail, type WorkOrderSiteItem } from '@/types/work-orders';

interface WorkOrderSitesTabProps {
  readonly workOrder: WorkOrderDetail;
  readonly canEdit: boolean;
}

function formatDateTime(d: string | null): string {
  if (d === null || d === '') return '—';
  try {
    return new Date(d).toLocaleString();
  } catch {
    return d;
  }
}

export function WorkOrderSitesTab({ workOrder, canEdit }: WorkOrderSitesTabProps): JSX.Element {
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<WorkOrderSiteItem | null>(null);

  const activeSites = (workOrder.sites ?? []).filter(isActiveSiteLink);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Sites</CardTitle>
          {canEdit && (
            <Button
              type="button"
              size="sm"
              onClick={() => { setAddOpen(true); }}
              data-testid="wo-sites-add-btn"
            >
              Add Sites
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table data-testid="wo-sites-table">
            <TableHeader>
              <TableRow>
                <TableHead>Site</TableHead>
                <TableHead>Linked At</TableHead>
                {canEdit && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeSites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 3 : 2} className="text-center py-6">
                    <span className="text-muted-foreground" data-testid="wo-sites-empty">
                      No active sites linked.
                    </span>
                  </TableCell>
                </TableRow>
              ) : (
                activeSites.map((s) => (
                  <TableRow key={s.id} data-testid={`wo-site-row-${String(s.siteId)}`}>
                    <TableCell>{s.siteName}</TableCell>
                    <TableCell>{formatDateTime(s.linkedAt)}</TableCell>
                    {canEdit && (
                      <TableCell>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => { setRemoveTarget(s); }}
                          data-testid={`wo-site-remove-${String(s.siteId)}`}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {addOpen && (
        <AddSitesDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          workOrder={workOrder}
        />
      )}

      {removeTarget !== null && (
        <RemoveSiteConfirmDialog
          open={true}
          onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
          workOrder={workOrder}
          site={removeTarget}
        />
      )}
    </Card>
  );
}
